import React from 'react';
import { 
  Target,
  GitBranch, 
  Globe, 
  AlertTriangle, 
  ChartBar,
  Shield,
  Code,
  CheckCircle,
  ArrowRight,
  Users
} from 'lucide-react';

// Composants de base
const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AnalysisPoint = ({ title, status = "info", children }) => {
  const statusColors = {
    success: "border-green-500 bg-green-50",
    warning: "border-yellow-500 bg-yellow-50",
    error: "border-red-500 bg-red-50",
    info: "border-blue-500 bg-blue-50"
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${statusColors[status]}`}>
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm space-y-2">{children}</div>
    </div>
  );
};

const Metric = ({ label, value, trend }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {trend && (
      <div className="text-sm text-gray-500 mt-1">{trend}</div>
    )}
  </div>
);

const OpenBankingAnalysis = () => {
  const projectOverview = {
    title: "API Hub BCEAO",
    phase: "Développement",
    timeline: "2024-2026",
    status: "Phase 1 - 30%",
    budget: "45M€",
    scope: "Zone UEMOA"
  };

  const objectives = [
    {
      title: "Infrastructure",
      items: [
        "API Gateway régional",
        "Portail développeur",
        "Infrastructure sécurité",
        "Monitoring & Analytics"
      ]
    },
    {
      title: "Services",
      items: [
        "APIs Comptes & Paiements",
        "APIs Open Data",
        "APIs Régulateurs",
        "APIs Innovation"
      ]
    },
    {
      title: "Écosystème",
      items: [
        "Communauté développeurs",
        "Place de marché APIs",
        "Sandbox test",
        "Support & Formation"
      ]
    }
  ];

  const technicalSpecs = {
    components: [
      {
        name: "API Gateway",
        description: "Plateforme centrale de routage et gestion APIs",
        features: [
          "Routage intelligent",
          "Rate limiting",
          "Transformation messages",
          "Monitoring temps réel"
        ],
        technologies: [
          "Kong/Nginx",
          "Redis",
          "Elasticsearch",
          "Prometheus"
        ]
      },
      {
        name: "Developer Portal",
        description: "Portail self-service développeurs",
        features: [
          "Documentation interactive",
          "Sandbox test",
          "Gestion accès",
          "Analytics usage"
        ],
        technologies: [
          "React/Node.js",
          "OpenAPI",
          "Keycloak",
          "Grafana"
        ]
      },
      {
        name: "Security Layer",
        description: "Infrastructure sécurité APIs",
        features: [
          "Authentification forte",
          "Autorisation granulaire",
          "Audit trail",
          "Détection fraude"
        ],
        technologies: [
          "OAuth 2.0/OIDC",
          "PKI/HSM",
          "SIEM",
          "AI/ML"
        ]
      }
    ],
    standards: [
      {
        name: "Protocoles",
        specs: [
          "REST/JSON",
          "ISO 20022",
          "JWT",
          "TLS 1.3"
        ]
      },
      {
        name: "Sécurité",
        specs: [
          "PSD2 SCA",
          "eIDAS",
          "GDPR",
          "PCI-DSS"
        ]
      }
    ]
  };
  const implementation = {
   phases: [
     {
       name: "Phase 1 - Foundation",
       period: "2024",
       status: "En cours",
       deliverables: [
         "Infrastructure API Gateway",
         "Sécurité socle",
         "APIs Comptes",
         "Portal V1"
       ]
     },
     {
       name: "Phase 2 - Extension",
       period: "2025",
       status: "Planifié",
       deliverables: [
         "APIs Paiements",
         "Open Data",
         "Analytics",
         "Support 24/7"
       ]
     },
     {
       name: "Phase 3 - Innovation",
       period: "2026",
       status: "Prévu",
       deliverables: [
         "APIs avancées",
         "Marketplace",
         "Analytics IA",
         "APIs Innovation"
       ]
     }
   ],
   governance: {
     committees: [
       "Comité stratégique",
       "Comité technique",
       "Groupe sécurité",
       "Forum développeurs"
     ],
     metrics: [
       "Disponibilité APIs",
       "Temps réponse",
       "Adoption",
       "Satisfaction"
     ]
   }
 };

 const impacts = {
   financial: [
     {
       metric: "Coûts IT",
       value: "-40%",
       trend: "vs. Legacy"
     },
     {
       metric: "Time-to-market",
       value: "-60%",
       trend: "Nouveaux services"
     },
     {
       metric: "Revenue API",
       value: "25M€",
       trend: "Projection 2026"
     }
   ],
   stakeholders: [
     {
       type: "Banques",
       benefits: [
         "Réduction coûts IT",
         "Innovation accélérée",
         "Nouveaux services",
         "Satisfaction client"
       ]
     },
     {
       type: "Fintechs",
       benefits: [
         "Accès marché",
         "Time-to-market",
         "Infrastructure mutualisée",
         "Support dédié"
       ]
     },
     {
       type: "Régulateurs",
       benefits: [
         "Supervision temps réel",
         "Standardisation",
         "Réduction risques",
         "Innovation contrôlée"
       ]
     }
   ]
 };

 const challenges = [
   {
     category: "Technique",
     items: [
       {
         challenge: "Performance & Scalabilité",
         impact: "Élevé",
         mitigation: "Architecture distribuée + Cache"
       },
       {
         challenge: "Sécurité APIs",
         impact: "Élevé",
         mitigation: "Zero Trust + Monitoring ML"
       }
     ]
   },
   {
     category: "Adoption",
     items: [
       {
         challenge: "Résistance changement",
         impact: "Moyen",
         mitigation: "Formation + Accompagnement"
       },
       {
         challenge: "Complexité intégration",
         impact: "Élevé",
         mitigation: "Documentation + Support"
       }
     ]
   }
 ];

 return (
   <div className="min-h-screen bg-gray-50 py-8">
     <div className="max-w-7xl mx-auto px-4">
       {/* Header */}
       <div className="mb-8">
         <h1 className="text-3xl font-bold mb-2">
           Open Banking UEMOA
         </h1>
         <div className="flex flex-wrap gap-4">
           <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
             Phase: {projectOverview.phase}
           </span>
           <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
             Timeline: {projectOverview.timeline}
           </span>
           <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
             Status: {projectOverview.status}
           </span>
         </div>
       </div>

       {/* Vision et Objectifs */}
       <Section title="Vision et Objectifs" icon={Target}>
         <div className="space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {objectives.map((obj, idx) => (
               <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                 <h3 className="font-medium mb-3">{obj.title}</h3>
                 <ul className="space-y-2">
                   {obj.items.map((item, i) => (
                     <li key={i} className="flex items-center text-sm">
                       <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                       {item}
                     </li>
                   ))}
                 </ul>
               </div>
             ))}
           </div>
         </div>
       </Section>

       {/* Architecture Technique */}
       <Section title="Architecture Technique" icon={GitBranch}>
         <div className="space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {technicalSpecs.components.map((component, idx) => (
               <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                 <h3 className="font-medium mb-2">{component.name}</h3>
                 <p className="text-sm text-gray-600 mb-3">{component.description}</p>
                 <div className="space-y-3">
                   <div>
                     <h4 className="text-sm font-medium mb-2">Fonctionnalités:</h4>
                     <ul className="space-y-1">
                       {component.features.map((feature, i) => (
                         <li key={i} className="flex items-center text-sm">
                           <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                           {feature}
                         </li>
                       ))}
                     </ul>
                   </div>
                   <div>
                     <h4 className="text-sm font-medium mb-2">Technologies:</h4>
                     <div className="flex flex-wrap gap-2">
                       {component.technologies.map((tech, i) => (
                         <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                           {tech}
                         </span>
                       ))}
                     </div>
                   </div>
                 </div>
               </div>
             ))}
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             {technicalSpecs.standards.map((standard, idx) => (
               <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                 <h3 className="font-medium mb-3">{standard.name}</h3>
                 <div className="flex flex-wrap gap-2">
                   {standard.specs.map((spec, i) => (
                     <span key={i} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                       {spec}
                     </span>
                   ))}
                 </div>
               </div>
             ))}
           </div>
         </div>
       </Section>

       {/* Plan d'implémentation */}
       <Section title="Plan d'Implémentation" icon={Shield}>
         <div className="space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {implementation.phases.map((phase, idx) => (
               <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                 <div className="flex justify-between items-start mb-3">
                   <h3 className="font-medium">{phase.name}</h3>
                   <span className={`px-2 py-1 rounded text-xs ${
                     phase.status === 'En cours'
                       ? 'bg-blue-100 text-blue-700'
                       : phase.status === 'Planifié'
                         ? 'bg-yellow-100 text-yellow-700'
                         : 'bg-gray-100 text-gray-700'
                   }`}>
                     {phase.status}
                   </span>
                 </div>
                 <p className="text-sm text-gray-600 mb-3">Période: {phase.period}</p>
                 <ul className="space-y-1">
                   {phase.deliverables.map((deliverable, i) => (
                     <li key={i} className="flex items-center text-sm">
                       <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                       {deliverable}
                     </li>
                   ))}
                 </ul>
               </div>
             ))}
           </div>

           <div className="bg-gray-50 p-4 rounded-lg">
             <h3 className="font-medium mb-4">Gouvernance</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div>
                 <h4 className="text-sm font-medium mb-2">Comités</h4>
                 <ul className="space-y-1">
                   {implementation.governance.committees.map((committee, idx) => (
                     <li key={idx} className="flex items-center text-sm">
                       <Users className="w-4 h-4 mr-2 text-blue-500" />
                       {committee}
                     </li>
                   ))}
                 </ul>
               </div>
               <div>
                 <h4 className="text-sm font-medium mb-2">Métriques</h4>
                 <ul className="space-y-1">
                   {implementation.governance.metrics.map((metric, idx) => (
                     <li key={idx} className="flex items-center text-sm">
                       <ChartBar className="w-4 h-4 mr-2 text-green-500" />
                       {metric}
                     </li>
                   ))}
                 </ul>
               </div>
             </div>
           </div>
         </div>
       </Section>

       {/* Impact */}
       <Section title="Impact Attendu" icon={ChartBar}>
         <div className="space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {impacts.financial.map((impact, idx) => (
               <Metric
                 key={idx}
                 label={impact.metric}
                 value={impact.value}
                 trend={impact.trend}
               />
             ))}
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
             {impacts.stakeholders.map((stakeholder, idx) => (
               <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                 <h3 className="font-medium mb-3">{stakeholder.type}</h3>
                 <ul className="space-y-2">
                   {stakeholder.benefits.map((benefit, i) => (
                     <li key={i} className="flex items-center text-sm">
                       <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                       {benefit}
                     </li>
                   ))}
                 </ul>
               </div>
             ))}
           </div>
         </div>
       </Section>

       {/* Défis */}
       <Section title="Défis et Risques" icon={AlertTriangle}>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
           {challenges.map((category, idx) => (
             <div key={idx}>
               <h3 className="font-medium mb-4">{category.category}</h3>
               <div className="space-y-4">
                 {category.items.map((item, i) => (
                   <div key={i} className="bg-white p-4 rounded-lg border border-gray-200">
                     <div className="flex justify-between items-center mb-2">
                       <span className="font-medium">{item.challenge}</span>
                       <span className={`px-2 py-1 rounded text-xs ${
                         item.impact === 'Élevé'
                           ? 'bg-red-100 text-red-700'
                           : 'bg-yellow-100 text-yellow-700'
                       }`}>
                         Impact: {item.impact}
                       </span>
                     </div>
                     <p className="text-sm text-gray-600">
                       Mitigation: {item.mitigation}
                     </p>
                   </div>
                 ))}
               </div>
             </div>
           ))}
         </div>
       </Section>
     </div>
   </div>
 );
};

export default OpenBankingAnalysis;